import React, { useState, useEffect, useContext } from 'react';
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  CardContent,
  Typography,
  Grid,
  Icon
} from '@material-ui/core';
import Http from '../../Http';
import Toast from 'react-hot-toast';
import moment from 'moment';
import { AuthContext } from '../../context/authContext';
import ACCESS from '../../ACCESS';

export default function IncidenciasManifiestos() {
  const authContext = useContext(AuthContext);

  const [incidencias, setIncidencias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, row: null, loading: false });

  // Filtros
  const [fechaInicio, setFechaInicio] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [fechaFin, setFechaFin] = useState(moment().format('YYYY-MM-DD'));
  const [estatusSeguimiento, setEstatusSeguimiento] = useState('Todos');
  const [tipoEntrega, setTipoEntrega] = useState('Todos');
  const [search, setSearch] = useState('');

  const canConfirmar = authContext?.is_admin || (authContext?.access && (
    authContext.access.includes(ACCESS.INCIDENCIAS_MANIFIESTOS?.CONFIRMAR_ENTERADO) ||
    authContext.access.includes(550001)
  ));

  const fetchIncidencias = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        estatus_seguimiento: estatusSeguimiento,
        tipo_entrega: tipoEntrega,
        search: search.trim()
      });
      const res = await Http.get(`incidencias-manifiestos?${params.toString()}`);
      if (res && !res.error && Array.isArray(res.data)) {
        setIncidencias(res.data);
      } else {
        setIncidencias([]);
        if (res && res.error) Toast.error(res.message || 'Error al obtener incidencias');
      }
    } catch (error) {
      console.error('Error fetching incidencias:', error);
      Toast.error('Error de comunicación con el servidor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncidencias();
  }, []);

  const handleOpenConfirm = (row) => {
    setConfirmModal({ open: true, row, loading: false });
  };

  const handleCloseConfirm = () => {
    setConfirmModal({ open: false, row: null, loading: false });
  };

  const handleMarcarEnterado = async () => {
    if (!confirmModal.row) return;
    setConfirmModal(prev => ({ ...prev, loading: true }));
    try {
      const res = await Http.post('incidencias-manifiestos/marcar-enterado', { id: confirmModal.row.id });
      if (res && !res.error) {
        Toast.success('Incidencia confirmada como Enterado exitosamente.');
        setIncidencias(prev => prev.map(item => {
          if (item.id === confirmModal.row.id) {
            return {
              ...item,
              estatus_seguimiento: 'Enterado',
              coordinador_enterado: authContext?.user?.nombre_completo || 'Coordinador',
              fecha_hora_enterado: res.data?.fecha_hora_enterado || new Date()
            };
          }
          return item;
        }));
        handleCloseConfirm();
      } else {
        Toast.error(res?.errMsg || res?.message || 'No se pudo confirmar el enterado');
        setConfirmModal(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error al marcar enterado:', error);
      Toast.error('Error inesperado al marcar enterado');
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: 15, color: '#1a202c' }}>
        Panel de Incidencias de Manifiestos / Remisiones
      </Typography>

      <Card variant="outlined" style={{ marginBottom: 20, backgroundColor: '#fcfcfc' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Fecha Desde"
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Fecha Hasta"
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Estatus Seguimiento</InputLabel>
                <Select
                  value={estatusSeguimiento}
                  onChange={(e) => setEstatusSeguimiento(e.target.value)}
                  label="Estatus Seguimiento"
                >
                  <MenuItem value="Todos">Todos</MenuItem>
                  <MenuItem value="Pendiente de enterado">Pendiente de enterado</MenuItem>
                  <MenuItem value="Enterado">Enterado</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small" variant="outlined">
                <InputLabel>Tipo de Entrega</InputLabel>
                <Select
                  value={tipoEntrega}
                  onChange={(e) => setTipoEntrega(e.target.value)}
                  label="Tipo de Entrega"
                >
                  <MenuItem value="Todos">Todos</MenuItem>
                  <MenuItem value="Incompleta">Incompleta</MenuItem>
                  <MenuItem value="Rechazo">Rechazo</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={8} md={3}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Buscar (Guía o Remisión)"
                placeholder="Ej. 0-123456 o 450002"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') fetchIncidencias(); }}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={1}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={fetchIncidencias}
                startIcon={<Icon>search</Icon>}
                style={{ height: 40 }}
              >
                Filtrar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper variant="outlined">
        <TableContainer style={{ maxHeight: 'calc(100vh - 280px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }}>Fecha / Hora</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Guía Exxprezo</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Remisión</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Cliente</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Tipo de Entrega</TableCell>
                <TableCell style={{ fontWeight: 'bold', minWidth: 200 }}>Motivo / Observación</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Transportista</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Estatus</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Coordinador Enterado</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Fecha Enterado</TableCell>
                <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" style={{ padding: 40 }}>
                    <CircularProgress size={36} />
                    <Typography variant="body2" style={{ marginTop: 10, color: '#666' }}>
                      Cargando incidencias...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : incidencias.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" style={{ padding: 30, color: '#888' }}>
                    No se encontraron incidencias con los criterios seleccionados.
                  </TableCell>
                </TableRow>
              ) : (
                incidencias.map((row) => {
                  const isPendiente = row.estatus_seguimiento === 'Pendiente de enterado';
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <span style={{ fontWeight: 500 }}>{row.fecha_registro}</span>
                        <div style={{ fontSize: 11, color: '#666' }}>{row.hora_registro}</div>
                      </TableCell>
                      <TableCell style={{ fontWeight: 600 }}>{row.g_folio}</TableCell>
                      <TableCell style={{ fontWeight: 600, color: '#1565c0' }}>{row.remision}</TableCell>
                      <TableCell>{row.cliente_nombre || row.cliente_codigo}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.tipo_entrega}
                          style={{
                            backgroundColor: row.tipo_entrega === 'Incompleta' ? '#fff3e0' : '#ffebee',
                            color: row.tipo_entrega === 'Incompleta' ? '#e65100' : '#c62828',
                            fontWeight: 'bold',
                            border: `1px solid ${row.tipo_entrega === 'Incompleta' ? '#ffb74d' : '#e57373'}`
                          }}
                        />
                      </TableCell>
                      <TableCell>{row.motivo || 'Sin motivo capturado'}</TableCell>
                      <TableCell>{row.transportista_nombre || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.estatus_seguimiento}
                          style={{
                            backgroundColor: isPendiente ? '#fff8e1' : '#e8f5e9',
                            color: isPendiente ? '#f57f17' : '#2e7d32',
                            fontWeight: 'bold',
                            border: `1px solid ${isPendiente ? '#ffe082' : '#a5d6a7'}`
                          }}
                        />
                      </TableCell>
                      <TableCell>{row.coordinador_enterado || '-'}</TableCell>
                      <TableCell>
                        {row.fecha_hora_enterado ? (
                          typeof row.fecha_hora_enterado === 'string' && row.fecha_hora_enterado.length <= 16
                            ? row.fecha_hora_enterado
                            : moment(row.fecha_hora_enterado).format('YYYY-MM-DD HH:mm')
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {isPendiente ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<Icon>check_circle</Icon>}
                            disabled={!canConfirmar}
                            onClick={() => handleOpenConfirm(row)}
                            style={{ textTransform: 'none', fontSize: 12 }}
                          >
                            Marcar como enterado
                          </Button>
                        ) : (
                          <Typography variant="caption" style={{ color: '#2e7d32', fontWeight: 600 }}>
                            Confirmado
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Modal de confirmación para Marcar como Enterado */}
      <Dialog open={confirmModal.open} onClose={handleCloseConfirm}>
        <DialogTitle>Confirmar Enterado de Incidencia</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Desea confirmar de enterado la incidencia de la remisión{' '}
            <strong>{confirmModal.row?.remision}</strong> perteneciente a la guía{' '}
            <strong>{confirmModal.row?.g_folio}</strong>?
          </DialogContentText>
          <Typography variant="body2" style={{ marginTop: 8, color: '#555' }}>
            <strong>Tipo de entrega:</strong> {confirmModal.row?.tipo_entrega}
          </Typography>
          <Typography variant="body2" style={{ color: '#555' }}>
            <strong>Motivo:</strong> {confirmModal.row?.motivo}
          </Typography>
          <Typography variant="caption" style={{ display: 'block', marginTop: 12, color: '#888', fontStyle: 'italic' }}>
            Nota: Al confirmar como enterado, se registrará su usuario y la fecha/hora actual como acuse de conocimiento de la situación.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="default" disabled={confirmModal.loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleMarcarEnterado}
            color="primary"
            variant="contained"
            disabled={confirmModal.loading}
            startIcon={confirmModal.loading ? <CircularProgress size={16} color="inherit" /> : <Icon>check_circle</Icon>}
          >
            Confirmar Enterado
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
